import React, { useEffect, useMemo, useState } from 'react'

import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import Alert from 'react-bootstrap/Alert'

import { formatCurrency } from '../../../../../utilities/helpers'

const parseAmount = (value) => {
  const amount = Number(String(value || '').replace(/[^0-9.]/g, ''))
  return Number.isFinite(amount) && amount > 0 ? amount : 0
}

const buildSelection = ({
  donationAmount = 0,
  anonymous = false,
  donorMessage = '',
  participantUuid = '',
}) => ({
  donationAmount,
  anonymous,
  donorMessage,
  participantUuid,
})

export default function FundraisingDonation({
  campaign,
  selection,
  onChange,
  disabled = false,
}) {
  const [customAmount, setCustomAmount] = useState('')

  const presetAmounts = useMemo(() => {
    const ruleAmounts = campaign?.donationRequirements?.suggestedAmounts
    if (Array.isArray(ruleAmounts) && ruleAmounts.length > 0) {
      return ruleAmounts
    }
    return Array.isArray(campaign?.suggestedAmounts)
      ? campaign.suggestedAmounts
      : []
  }, [campaign])

  const requirements = campaign?.donationRequirements || {}
  const minimumAmount = Number(requirements.minimumAmount || 0)
  const isMandatory = Boolean(requirements.mandatory)

  useEffect(() => {
    if (!campaign) return
    const presetMatch = presetAmounts.find(
      (amount) => parseAmount(amount) === Number(selection?.donationAmount || 0)
    )
    if (!presetMatch && Number(selection?.donationAmount || 0) > 0) {
      setCustomAmount(String(selection.donationAmount))
    }
  }, [campaign, presetAmounts, selection?.donationAmount])

  if (!campaign) return null

  const selectedAmount = Number(selection?.donationAmount || 0)
  const participants = Array.isArray(campaign.participants)
    ? campaign.participants
    : []

  const emitSelection = (updates) => {
    onChange(
      buildSelection({
        donationAmount: selectedAmount,
        anonymous: Boolean(selection?.anonymous),
        donorMessage: selection?.donorMessage || '',
        participantUuid: selection?.participantUuid || '',
        ...updates,
      })
    )
  }

  const selectPreset = (amountLabel) => {
    setCustomAmount('')
    emitSelection({ donationAmount: parseAmount(amountLabel) })
  }

  const applyCustomAmount = () => {
    const nextAmount = parseAmount(customAmount)
    if (isMandatory && minimumAmount > 0 && nextAmount < minimumAmount) return
    emitSelection({ donationAmount: nextAmount })
  }

  const clearDonation = () => {
    if (isMandatory) return
    setCustomAmount('')
    emitSelection({ donationAmount: 0 })
  }

  return (
    <Card className="card-xl card-xl--light mb-4">
      <Card.Body>
        {isMandatory && (
          <Alert variant="warning" className="mb-3">
            <strong>{requirements.ruleName || 'Donation required'}:</strong> A
            minimum donation of {formatCurrency(minimumAmount)} is required for
            tickets in the selected sections.
            {requirements.satisfiedByCarryForward
              ? ' A prior donation this year already satisfies this requirement.'
              : ''}
          </Alert>
        )}
        <Card.Title as="h5" className="card-title-sm">
          {campaign.donationTitle || campaign.title}
        </Card.Title>
        {campaign.donationDescription && (
          <p className="text-muted mb-3">{campaign.donationDescription}</p>
        )}

        {participants.length > 0 && (
          <Form.Group className="mb-3">
            <Form.Label>Support</Form.Label>
            <Form.Select
              value={selection?.participantUuid || ''}
              disabled={disabled}
              onChange={(event) =>
                emitSelection({ participantUuid: event.target.value })
              }
            >
              <option value="">General campaign fund</option>
              {participants.map((participant) => (
                <option key={participant.uuid} value={participant.uuid}>
                  {participant.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        <p className="small fw-semibold mb-2">Add a donation</p>
        <Stack direction="horizontal" gap={2} className="flex-wrap mb-3">
          {presetAmounts.map((amount) => {
            const parsed = parseAmount(amount)
            const isSelected =
              selectedAmount > 0 && selectedAmount === parsed && !customAmount
            return (
              <Button
                key={amount}
                variant={isSelected ? 'primary' : 'outline-light'}
                size="sm"
                disabled={disabled}
                onClick={() => selectPreset(amount)}
              >
                {amount}
              </Button>
            )
          })}
          {selectedAmount <= 0 && (
            <Badge bg="light" text="dark">
              No donation
            </Badge>
          )}
        </Stack>

        {campaign.allowCustomAmount !== false &&
          requirements.allowCustomAmount !== false && (
            <Stack direction="horizontal" gap={2} className="mb-3">
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                placeholder="Custom amount"
                value={customAmount}
                disabled={disabled}
                onChange={(event) => setCustomAmount(event.target.value)}
              />
              <Button
                variant="outline-light"
                disabled={disabled || !parseAmount(customAmount)}
                onClick={applyCustomAmount}
              >
                Apply
              </Button>
            </Stack>
          )}

        {selectedAmount > 0 && !isMandatory && (
          <div className="mb-3">
            <span className="small text-muted">
              Selected donation: {formatCurrency(selectedAmount)}
            </span>
            <Button
              variant="link"
              size="sm"
              className="p-0 ms-2"
              disabled={disabled}
              onClick={clearDonation}
            >
              Remove
            </Button>
          </div>
        )}
        {isMandatory && selectedAmount > 0 && (
          <div className="small text-muted mb-3">
            Selected donation: {formatCurrency(selectedAmount)}
          </div>
        )}

        {campaign.allowAnonymousDonation && (
          <Form.Check
            type="checkbox"
            className="mb-2"
            label="Donate anonymously"
            checked={Boolean(selection?.anonymous)}
            disabled={disabled || selectedAmount <= 0}
            onChange={(event) =>
              emitSelection({ anonymous: event.target.checked })
            }
          />
        )}

        {campaign.allowDonorMessage && (
          <Form.Group>
            <Form.Label>Message (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={selection?.donorMessage || ''}
              disabled={disabled || selectedAmount <= 0}
              onChange={(event) =>
                emitSelection({ donorMessage: event.target.value })
              }
            />
          </Form.Group>
        )}
      </Card.Body>
    </Card>
  )
}
